//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { IAugmentedJQuery, IControllerConstructor, Injectable } from 'angular';
import React, { ComponentType } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { ThemeProvider } from 'styled-components';

import { ICuralateStore } from '@models/AppState';
import { ReactQueryProvider } from '@queries/ReactQueryProvider';

/**
 * Creates an Angular Component Controller that initializes a new React App with the given component.
 * @param component React Component/HTML to initialize
 */
export default function makeAngularReactControllerWithChild(
  TargetComponent: ComponentType,
  className?: string,
): Injectable<IControllerConstructor> {
  return [
    '$ngRedux',
    '$element',
    ($ngRedux: Store<ICuralateStore>, $element: IAugmentedJQuery) => {
      const node = $element[0] ?? null;
      const innerHTML = node?.innerHTML;

      ReactDOM.render(
        <ThemeProvider theme={theme}>
          <Provider store={$ngRedux}>
            <ReactQueryProvider>
              {innerHTML ? (
                <TargetComponent>
                  <div
                    className={className}
                    dangerouslySetInnerHTML={{ __html: innerHTML }}
                  />
                </TargetComponent>
              ) : (
                <TargetComponent />
              )}
            </ReactQueryProvider>
          </Provider>
        </ThemeProvider>,
        node,
      );

      return {
        $onDestroy: () => {
          if (node) {
            ReactDOM.unmountComponentAtNode(node);
          }
        },
      };
    },
  ];
}
